import './PercentileRankings.scss';

import { PercentileRankingsChartDto, PercentileRankingValue, PercentileRankingView } from 'types/CodegenReportTypes';

import ProgressBar from 'components/ProgressBar/ProgressBar';
import { Typography } from '@trackman/web-shared-components';
import { Fragment, useCallback } from 'react';
import { useChartDataDisplay, useGetUnitSystem } from 'hooks/helperHooks';
import { useTranslation } from 'react-i18next';
import { PercentileRankingConfig, percentileRankings } from 'utilities/percentileRankings';
import { getOrderedColorSet } from 'utilities/colorHelpers';

type Props = {
  chartData: PercentileRankingsChartDto | undefined;
  isLoading: boolean;
  displayKeys: string[];
  viewDataAs: PercentileRankingView;
};

const PercentileRankings = ({ chartData, isLoading, displayKeys, viewDataAs }: Props) => {
  const simulateChartData = useCallback(
    (isLoading: boolean) => {
      const simulatedChartData = {} as Partial<Omit<PercentileRankingsChartDto, 'chartId'>>;
      displayKeys.forEach((key) => {
        if (!isLoading) {
          simulatedChartData[key] = null;
        } else {
          const random = Math.random() * 100;
          simulatedChartData[key] = { percentile: random, value: random };
        }
      });
      return simulatedChartData;
    },
    [displayKeys]
  );

  const chartDataDisplay = useChartDataDisplay(chartData, simulateChartData, isLoading);

  return (
    <div className='PercentileRankings'>
      {displayKeys.map((key, i, arr) => {
        const value = chartDataDisplay[key] as PercentileRankingValue | null;
        return (
          <Fragment key={key}>
            <PercentileRankingRow key={key} value={value} label={key} viewDataAs={viewDataAs} />
            {i < arr.length - 1 && <PillIndicator key={key + 'indicator'} />}
          </Fragment>
        );
      })}
    </div>
  );
};

const PercentileRankingRow = ({
  value,
  label,
  viewDataAs,
}: {
  value: PercentileRankingValue | null;
  label: string;
  viewDataAs: PercentileRankingView;
}) => {
  const { t } = useTranslation();
  const config = percentileRankings[label] as PercentileRankingConfig;
  const unitSystem = useGetUnitSystem();

  function getFormattedValue() {
    if (viewDataAs === 'Percentiles') {
      return value?.percentile ? Math.round(value.percentile) : '-';
    }

    let suffix = '';
    if (typeof config.unitLabel === 'function') {
      suffix = ' ' + config.unitLabel(unitSystem);
    } else if (config.unitLabel) {
      suffix = ' ' + config.unitLabel;
    }

    if (value == null) {
      return '-' + suffix;
    }

    const roundedValue = config.fractionDigits !== undefined ? value.value.toFixed(config.fractionDigits) : String(value.value);
    const formattedValue = roundedValue !== '0' && !config.leadingZero ? roundedValue.replace(/^0+/, '') : roundedValue;
    return formattedValue + suffix;
  }

  return (
    <div className='PercentileRankingRow'>
      <Typography component='div' className='label'>
        {t(`short.${label}`)}
      </Typography>
      <ProgressBar percentile={value?.percentile ?? 0} backgroundColor={getOrderedColorSet(0, 100, value?.percentile ?? 0).bg} />
      <Typography component='div' className='percentile'>
        {getFormattedValue()}
      </Typography>
    </div>
  );
};

const PillIndicator = () => {
  return (
    <div className='PercentileRankingRow PercentileRankingRow-indicators'>
      <div className='label' />
      <div className='pill-indicators'>
        <div className='pill-indicator-left' />
        <div className='pill-indicator-right' />
      </div>
      <div className='percentile' />
    </div>
  );
};

export default PercentileRankings;
